"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.moveNode = exports.findFirstStepId = exports.findFirstNode = exports.hasEndNode = exports.hasABTestNode = exports.copyFlowPath = exports.getStepIds = exports.findPreviousStepIds = exports.findPreviousNodes = void 0;
const assert_1 = __importDefault(require("assert"));
const types_1 = require("@upflowy/types");
const helper_1 = require("./helper");
/**
 *
 * @param path
 * @param nodeId
 * @param includeNode Include nodeId in result
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function findPreviousNodes(path, nodeId, includeNode = false) {
    const nodes = [];
    for (let i = 0; i < path.length; i += 1) {
        const node = path[i];
        (0, assert_1.default)(node);
        if (!node)
            throw new Error('node should be defined');
        if (node.id === nodeId && !includeNode) {
            return [nodes, true];
        }
        nodes.push(node);
        if (node.type === 'condNode' || node.type === 'abTestNode') {
            const condNodes = [];
            for (let i1 = 0; i1 < node.paths.length; i1 += 1) {
                const condPath = node.paths[i1];
                (0, assert_1.default)(condPath);
                const [prevNodes, found] = findPreviousNodes(condPath.path, nodeId, includeNode);
                if (found) {
                    return [[...nodes, ...prevNodes], true];
                }
                condNodes.push(...prevNodes);
            }
            nodes.push(...condNodes);
        }
        if (node.id === nodeId) {
            return [nodes, true];
        }
    }
    return [nodes, false];
}
exports.findPreviousNodes = findPreviousNodes;
/**
 *
 * @param path
 * @param nodeId
 * @param includeNode Include nodeId in result
 */
function findPreviousStepIds(path, nodeId, includeNode = false) {
    const [nodes] = findPreviousNodes(path, nodeId, includeNode);
    return nodes
        .filter((n) => n.type === 'stepNode')
        .map((n) => { var _a; return (_a = n === null || n === void 0 ? void 0 : n.config) === null || _a === void 0 ? void 0 : _a.stepId; })
        .filter(types_1.helper.isNotUndefined);
}
exports.findPreviousStepIds = findPreviousStepIds;
function getStepIds(machineConfig) {
    return (machineConfig === null || machineConfig === void 0 ? void 0 : machineConfig.states)
        ? Object.values(machineConfig === null || machineConfig === void 0 ? void 0 : machineConfig.states)
            .map((state) => { var _a, _b; return state.meta && 'stepId' in state.meta && ((_a = state.meta) === null || _a === void 0 ? void 0 : _a.stepId) ? (_b = state.meta) === null || _b === void 0 ? void 0 : _b.stepId : ''; })
            .filter((stepId) => stepId !== '')
        : [];
}
exports.getStepIds = getStepIds;
async function copyFlowPath(path, duplicateStep) {
    return Promise.all(path.map(async (node) => {
        var _a, _b;
        if (node.type === 'condNode' || node.type === 'abTestNode') {
            const id = (0, helper_1.shortUuid)();
            return {
                ...node,
                id,
                paths: await Promise.all(node.paths.map(async (p) => ({
                    ...p,
                    path: await copyFlowPath(p.path, duplicateStep),
                }))),
                splitNode: node.id === node.splitNode ? id : (0, helper_1.shortUuid)(),
                joinNode: (0, helper_1.shortUuid)(),
            };
        }
        if (node.type === 'stepNode' && ((_a = node.config) === null || _a === void 0 ? void 0 : _a.stepId)) {
            const stepId = (await duplicateStep((_b = node.config) === null || _b === void 0 ? void 0 : _b.stepId)).id;
            return {
                ...node,
                id: stepId,
                config: {
                    ...node.config,
                    stepId,
                },
            };
        }
        return { ...node, id: (0, helper_1.shortUuid)() };
    }));
}
exports.copyFlowPath = copyFlowPath;
/**
 * Returns true if the path contains an ABTestNode
 * @param path Complete path
 */
const hasABTestNode = (path) => {
    const firstAbTestNode = findFirstNode(path, (nodePath) => nodePath.type === 'abTestNode');
    return !!firstAbTestNode;
};
exports.hasABTestNode = hasABTestNode;
function hasEndNode(path) {
    const lastNode = path[path.length - 1];
    if ((lastNode === null || lastNode === void 0 ? void 0 : lastNode.type) === 'condNode') {
        return lastNode.paths.every((p) => hasEndNode(p.path));
    }
    return (lastNode === null || lastNode === void 0 ? void 0 : lastNode.type) === 'endNode';
}
exports.hasEndNode = hasEndNode;
function findFirstNode(path, predicate) {
    for (let i = 0; i < path.length; i += 1) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const node = path[i];
        if (predicate(node))
            return node;
        if (node.type === 'condNode' || node.type === 'abTestNode') {
            for (let i1 = 0; i1 < node.paths.length; i1 += 1) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const p = node.paths[i1];
                const found = findFirstNode(p.path, predicate);
                if (found)
                    return found;
            }
        }
    }
    return undefined;
}
exports.findFirstNode = findFirstNode;
function findFirstStepId(path) {
    var _a;
    const node = findFirstNode(path, (n) => n.type === 'stepNode');
    if (!node)
        return undefined;
    return (_a = node.config) === null || _a === void 0 ? void 0 : _a.stepId;
}
exports.findFirstStepId = findFirstStepId;
function flowPathMapper(path, pathMapper) {
    const newPath = pathMapper(path);
    return newPath.map((node) => {
        if ('paths' in node) {
            return {
                ...node,
                paths: node.paths.map((p) => ({
                    ...p,
                    path: flowPathMapper(p.path, pathMapper),
                })),
            };
        }
        return node;
    });
}
/**
 *
 * @param path Complete path
 * @param node Node to be moved
 * @param insertionPath Path where node is to be inserted
 * @param atNode Node following insertion point. If undefined insert at begging of path
 */
function moveNode(path, node, insertionPath, atNode) {
    if (atNode === node)
        return path;
    return flowPathMapper(path, (subPath) => {
        let newPath = subPath;
        if (newPath.includes(node)) {
            newPath = newPath.filter((n) => n !== node);
        }
        if (subPath === insertionPath) {
            const index = atNode ? newPath.indexOf(atNode) : -1;
            newPath = [...newPath.slice(0, index + 1), node, ...newPath.slice(index + 1)];
        }
        return newPath;
    });
}
exports.moveNode = moveNode;
//# sourceMappingURL=xstate-path-mapper.js.map